<template>
  <SmDialog
    v-model:visibility="showAwaitAuthDialog"
    :title="i18n.t('authPending.title')">
    <p>
      {{
        i18n.t('authPending.description', {
          software: activeApplication?.display_name,
          time: 24,
        })
      }}
    </p>
    <template #footer>
      <SmButton outline @click="handleBackToApplications">
        {{ i18n.t('buttonBackToApplication') }}
      </SmButton>
    </template>
  </SmDialog>

  <ExportDialog
    v-model:visibility="visibilityExportDialog"
    v-model:sub-account-id="subAccountId"
    :account-filter="licenseTable?.accountFilter"
    :application-id="activeApplication?._id" />

  <SmDialog
    v-model:visibility="showLicenseImportDialog"
    :title="i18n.t('licenseCsvImport.csvImportHeading')">
    <AccountCsvImport
      v-if="activeApplication"
      :application-id="activeApplication._id"
      @successful-import="handleSuccessfulImport" />
  </SmDialog>

  <AccountAddDialog
    v-if="activeApplication"
    v-model:visibility="visibilityAccountAddDialog"
    :software-licenses="softwareLicensesWithSelectedSubAccount"
    :application="activeApplication"
    :sub-account="currentSubAccount"
    @user-added="handleRefetchLicences()" />

  <ApplicationDetailDrawer
    v-if="activeApplication"
    v-model:show-drawer="showApplicationEditDialog"
    v-model:tab="licenceViewMode"
    v-model:application="activeApplication"
    :current-licences="licenses"
    :selected-sub-account-id="
      applicationDetailsStore.$state.selectedSubAccount?.id || null
    "
    @software-changed="handleRefetchApplication()"
    @user-saved="handleRefetchLicences()" />

  <div class="application-details-grid h-full">
    <div class="header-card">
      <ApplicationDetailsHeader
        ref="statsCard"
        data-tutorial-id="application-details-stats-card"
        :application="activeApplication"
        @open-application-details-drawer="
          (tabName: string) => (
            (showApplicationEditDialog = true), (licenceViewMode = tabName)
          )
        " />
    </div>

    <!-- Account Table -->
    <el-card
      class=""
      style="grid-area: licensesCard"
      :body-style="{
        height: '100%',
        width: '100%',
      }">
      <ApplicationDetailsLicencesTable
        v-if="activeApplication && currentSubAccount"
        ref="licenseTable"
        v-model:sub-account="currentSubAccount"
        :show-import-export="false"
        data-tutorial-id="application-details-licences-card"
        class="licenses-card"
        :application="activeApplication"
        :application-licenses="allSoftwareLicenses"
        @export-licenses="visibilityExportDialog = true"
        @licenses-changed="handleLicensesChanged"
        @open-add-user="visibilityAccountAddDialog = true"
        @import-licenses="showLicenseImportDialog = true"
        @open-application-details-drawer="
          (tabName: string) => (
            (showApplicationEditDialog = true), (licenceViewMode = tabName)
          )
        " />
    </el-card>

    <!-- Combo Card -->
    <el-card
      class="combo-card"
      :body-style="{
        height: '100%',
      }">
      <el-tabs v-model="comboCardActiveName" class="h-full">
        <!-- License Model Edit -->
        <el-tab-pane
          v-for="item in availableSubTypes"
          :key="item"
          :label="item"
          :name="item"
          lazy
          :style="tabPaneStyle">
          <div
            v-show="allSoftwareLicenses.length > 0 || loadingLicences"
            style="flex-grow: 1; position: relative">
            <LicenseModelEditTable
              ref="licenseModelEditTable"
              :software-licenses="
                allSoftwareLicenses.filter((r) => {
                  if (item === 'License' && r.subtype === null) {
                    return true
                  }
                  return r.subtype === item
                })
              "
              hide-header
              :application="activeApplication"
              :selectable="false"
              licenses-editable
              show-add-row
              :loading="loadingLicences"
              :sub-account-id="currentSubAccount?.id"
              @software-changed="handleRefetchApplication()"
              @licenses-changed="fetchData()" />
          </div>

          <div
            v-if="allSoftwareLicenses.length === 0 && !loadingLicences"
            class="mb-3 flex h-40 items-center justify-center rounded border-2 border-dashed border-gray-300 p-8">
            <SmButton outline @click="handleAddLicenseClick">
              <Plus class="mr-2" :size="18" />
              {{ i18n.t('addLicense') }}
            </SmButton>
          </div>
        </el-tab-pane>
      </el-tabs>
    </el-card>
  </div>
</template>

<script lang="ts" setup>
  import { computed, ref, watch } from 'vue'
  import { ComponentExposed } from 'vue-component-type-helpers'
  import { useI18n } from 'vue-i18n'
  import { useRoute, useRouter } from 'vue-router'
  import { useDocumentVisibility } from '@vueuse/core'

  import { SoftwareLicense, SoftwareOut, SubAccount_Output } from '@/client'

  import LicenseModelEditTable from '@/components/license/LicenseModelEditTable.vue'
  import { useApplicationDetailsStore } from '@/stores/ApplicationDetailsView.store'
  import ApplicationDetailsHeader from '@/components/applications/details/ApplicationDetailsHeader.vue'
  import ApplicationDetailsLicencesTable from '@/components/applications/details/licenseTable/ApplicationDetailsLicencesTable.vue'
  import { ApplicationStore } from '@/stores/application/application.store'
  import { Plus } from 'lucide-vue-next'
  import {
    normalizeSubAccountParam,
    selectPreferredSubAccount,
  } from '@/utils/useSubAccountResolver'

  const i18n = useI18n()

  const applicationDetailsStore = useApplicationDetailsStore()
  const route = useRoute()
  const router = useRouter()
  const showLicenseImportDialog = ref(false)
  const showAwaitAuthDialog = ref(false)
  const statsCard = ref<ComponentExposed<typeof ApplicationDetailsHeader>>()

  const licenseTable =
    ref<ComponentExposed<typeof ApplicationDetailsLicencesTable>>()
  const licenseModelEditTable =
    ref<ComponentExposed<typeof LicenseModelEditTable>[]>()
  const visibilityAccountAddDialog = ref(false)
  const visibilityExportDialog = ref<boolean>(false)

  const loadingLicences = computed(
    () => applicationDetailsStore.loadingLicences
  )

  // Combo Card
  const comboCardActiveName = ref('License')

  function handleAddLicenseClick() {
    licenseModelEditTable.value?.[0]?.openAddLicense()
  }

  const tabPaneStyle = {
    height: '100%',
    display: 'flex',
    'flex-direction': 'column',
    overflowY: 'auto',
  }

  watch(
    () => route.params.id,
    (newId) => {
      const resolvedId = Array.isArray(newId) ? newId[0] : (newId as string)
      if (resolvedId) {
        applicationDetailsStore.loadApplicationDetails(resolvedId)
      }
    },
    { immediate: true }
  )

  // ******************************* Application *******************************

  const currentSubAccount = ref<SubAccount_Output>()
  const requestedSubAccountId = computed(() =>
    normalizeSubAccountParam(route.params.subAccountId)
  )
  const subAccountId = computed(() => currentSubAccount.value?.id)

  // After reauth check if application is autherized now
  const docVisibility = useDocumentVisibility()
  watch(docVisibility, (current, previous) => {
    if (current === 'visible' && previous === 'hidden') {
      ApplicationStore.updateApplicationCacheFromApi(true)
    }
  })

  // Get the Application name
  const { data: application } = ApplicationStore.useApplications<SoftwareOut>({
    id: route.params.id as string,
  })

  const activeApplication = computed(() => application.value?.[0])

  const availableSubTypes = computed(() => {
    const subtypes = applicationDetailsStore.licences
      .map((license) => license.subtype)
      .filter((subtype): subtype is string => subtype !== null)

    return new Set(subtypes).add('License')
  })

  // Refetch the application data
  function updateSelectedSubAccount(app: SoftwareOut, requestedId?: string) {
    if (!app.sub_accounts) {
      currentSubAccount.value = undefined
      applicationDetailsStore.selectedSubAccount = undefined
      return
    }

    const selection = selectPreferredSubAccount(app.sub_accounts, requestedId)

    if (!selection) {
      currentSubAccount.value = undefined
      applicationDetailsStore.selectedSubAccount = undefined
      return
    }

    currentSubAccount.value = selection.subAccount as SubAccount_Output
    applicationDetailsStore.selectedSubAccount =
      selection.subAccount as SubAccount_Output
  }

  watch(
    [application, requestedSubAccountId],
    ([newApplications, requestedId]) => {
      if (!newApplications || newApplications.length === 0) return

      const activeApplication = newApplications[0]
      applicationDetailsStore.application = activeApplication

      updateSelectedSubAccount(activeApplication, requestedId)
    },
    { immediate: true }
  )

  const allSoftwareLicenses = computed<SoftwareLicense[]>(() => {
    return applicationDetailsStore.licences
  })

  const softwareLicensesWithSelectedSubAccount = computed(() => {
    return allSoftwareLicenses.value.filter(
      (license) =>
        license.sub_account_id === currentSubAccount.value?.id ||
        license.sub_account_id === null
    )
  })

  // ******************************* USER *******************************

  const showApplicationEditDialog = ref(route.query.edit === 'true' || false)
  const licenceViewMode = ref('EditSoftware')

  function handleSuccessfulImport() {
    showLicenseImportDialog.value = false
    handleRefetchLicences()
  }

  // reload application data to update the displayed values in the basic-information
  async function handleRefetchLicences() {
    licenseTable.value?.handleLicencesRefetch({ cached: false })
  }

  async function fetchData() {
    if (licenseTable.value) licenseTable.value.refetchSoftwareLicenses()

    // check if auth is pending
    if (activeApplication.value?.awaits_authorization) {
      showAwaitAuthDialog.value = true
    }

    if (!activeApplication.value?._id) return

    applicationDetailsStore.initApplication(activeApplication.value)
    await applicationDetailsStore.loadSoftwareLicenses(
      activeApplication.value._id,
      true
    )
  }

  watch(
    () => activeApplication.value?._id,
    (id, oldId) => {
      if (!id || id === oldId) return
      // Only fetch data if we're actually on the overview tab
      if (route.name === 'ApplicationDetailsOverview') {
        fetchData()
      }
    }
  )

  // ******************************* Hooks *******************************
  // Note: We don't reset the store here because it's shared across tabs
  // and should persist when switching between application detail views

  // ******************************* Functions *******************************
  async function handleRefetchApplication() {
    await applicationDetailsStore.refreshLicences()
  }

  function handleLicensesChanged() {
    // Refetch the statistics
    if (activeApplication.value?._id) {
      statsCard.value?.fetchStatistics(activeApplication.value._id)
    }
  }

  function handleBackToApplications() {
    showAwaitAuthDialog.value = false
    router.push({ path: '/applications' })
  }

  const licenses = computed(() => {
    return licenseTable.value?.licenses
  })
</script>

<style lang="scss" scoped>
  /* Header */
  .with-back-arrow {
    display: flex;
    flex-direction: row;
    align-items: center;
    cursor: pointer;
  }

  .back-arrow {
    transition: all 0.4s ease-in-out;
  }

  .with-back-arrow:hover .back-arrow {
    color: var(--el-color-primary);
    animation: back-button-hover;
    animation-duration: 2s;
    animation-iteration-count: infinite;
  }

  @keyframes back-button-hover {
    0% {
      transform: scale(1);
    }

    50% {
      transform: scale(1.2);
    }

    100% {
      transform: scale(1);
    }
  }

  .application-details-grid {
    display: grid;
    width: 100%;
    gap: var(--gap-lg);
    grid-template-columns: repeat(6, 1fr);
    grid-template-rows:
      auto minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr)
      minmax(0, 1fr) minmax(0, 1fr);
    grid-template-areas:
      'headerCard headerCard headerCard headerCard headerCard headerCard'
      'licensesCard licensesCard licensesCard licensesCard comboCard comboCard'
      'licensesCard licensesCard licensesCard licensesCard comboCard comboCard'
      'licensesCard licensesCard licensesCard licensesCard comboCard comboCard'
      'licensesCard licensesCard licensesCard licensesCard comboCard comboCard'
      'licensesCard licensesCard licensesCard licensesCard comboCard comboCard'
      'licensesCard licensesCard licensesCard licensesCard comboCard comboCard';

    .header-card {
      grid-area: headerCard;
    }

    .licenses-card {
      grid-area: licensesCard;
    }

    .savings-card {
      grid-area: savingsCard;
    }

    .combo-card {
      grid-area: comboCard;
    }
  }
</style>

<style lang="scss">
  .application-details-grid {
    .el-tabs__content {
      height: 100%;
    }
  }
</style>
